import {
	action,
	computed,
	makeObservable,
	observable,
	runInAction
} from 'mobx';
import { directoryAxiosClient, lmsAxiosClient } from 'Services/Api/client';
import DIRECTORY_API from 'Services/Api/Directory/Api';
import { DirectoryLmsInstance } from 'Services/Api/Directory/Types';
import USER_DASHBOARD_API from 'Services/Api/UserDashboard/Api';
import {
	ActivityAssignment,
	LearningPathAssignment,
	ListActivityAssignmentsParams,
	ListActivityAssignmentsResponse,
	ListLearningPathAssignmentsParams,
	ListLearningPathAssignmentsResponse
} from 'Services/Api/UserDashboard/Types';
import { Status } from 'app-types';

class UserDashboardStore {
	status = {
		listLearningPathAssignments: Status.Idle,
		listActivityAssignments: Status.Idle,
		listLmsInstances: Status.Idle
	};

	learningPathAssignments: LearningPathAssignment[] = [];
	learningPathAssignmentsTotal = 0;
	activityAssignments: ActivityAssignment[] = [];
	activityAssignmentsTotal = 0;
	lmsInstances: DirectoryLmsInstance[] = [];

	constructor() {
		makeObservable(this, {
			status: observable,
			learningPathAssignments: observable,
			learningPathAssignmentsTotal: observable,
			activityAssignments: observable,
			activityAssignmentsTotal: observable,
			lmsInstances: observable,
			activityAssignmentsById: computed,
			lmsInstancesById: computed
		});
	}

	get activityAssignmentsById(): Record<number, ActivityAssignment> {
		return this.activityAssignments.reduce<Record<number, ActivityAssignment>>(
			(acc, activity) => {
				acc[activity.id] = activity;
				return acc;
			},
			{}
		);
	}

	get lmsInstancesById(): Record<number, DirectoryLmsInstance> {
		return this.lmsInstances.reduce<Record<number, DirectoryLmsInstance>>(
			(acc, instance) => {
				acc[instance.id] = instance;
				return acc;
			},
			{}
		);
	}

	@action
	async fetchLearningPathAssignments(
		params: ListLearningPathAssignmentsParams = { offset: 0 }
	): Promise<void> {
		runInAction(() => {
			this.status.listLearningPathAssignments = Status.Pending;
		});
		try {
			const {
				data
			} = await lmsAxiosClient.get<ListLearningPathAssignmentsResponse>(
				USER_DASHBOARD_API.LEARNING_PATH_ASSIGNMENTS,
				{ params }
			);
			runInAction(() => {
				this.learningPathAssignments = data.data;
				this.learningPathAssignmentsTotal = data.meta.total;
				this.status.listLearningPathAssignments = Status.Success;
			});
		} catch (e) {
			runInAction(() => {
				this.status.listLearningPathAssignments = Status.Failure;
			});
			throw e;
		}
	}

	@action
	async fetchActivityAssignments(
		params: ListActivityAssignmentsParams = { offset: 0 }
	): Promise<void> {
		runInAction(() => {
			this.status.listActivityAssignments = Status.Pending;
		});
		try {
			const {
				data
			} = await lmsAxiosClient.get<ListActivityAssignmentsResponse>(
				USER_DASHBOARD_API.ACTIVITY_ASSIGNMENTS,
				{ params }
			);
			runInAction(() => {
				this.activityAssignments = data.data;
				this.activityAssignmentsTotal = data.meta.total;
				this.status.listActivityAssignments = Status.Success;
			});
		} catch (e) {
			runInAction(() => {
				this.status.listActivityAssignments = Status.Failure;
			});
			throw e;
		}
	}

	@action
	async fetchLmsInstances(): Promise<void> {
		runInAction(() => {
			this.status.listLmsInstances = Status.Pending;
		});
		try {
			const {
				data
			} = await directoryAxiosClient.get<DirectoryLmsInstance[]>(
				DIRECTORY_API.LMS_INSTANCES
			);
			runInAction(() => {
				this.lmsInstances = data;
				this.status.listLmsInstances = Status.Success;
			});
		} catch (e) {
			runInAction(() => {
				this.status.listLmsInstances = Status.Failure;
			});
			throw e;
		}
	}

	// Opens an activity in the LMS SCORM player popup.
	// {lms_instance.url}/scorm_player.php?catalog_course_id={lms_activity_id}
	launchActivity(activity: ActivityAssignment): void {
		const instance = this.lmsInstancesById[activity.lms_instance_id];
		if (!instance) return;

		const url = `${instance.url}/scorm_player.php?catalog_course_id=${activity.lms_activity_id}`;

		window.open(
			url,
			'_blank',
			`width=${activity.popup_width}, height=${activity.popup_height}, resizable=yes, scrollbars=yes, menubar=no, status=no`
		);
	}
}

export const userDashboardStore = new UserDashboardStore();
export default UserDashboardStore;
